import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createSetupIntent,
  getBillingInfo,
  getInvoices,
  getSubscriptions,
  unsubscribe,
} from '../api';
import { subscriptionsQueries } from '../data';

const useInvoices = (companyID: string) => {
  return useQuery({
    queryKey: [...subscriptionsQueries.getInvoices, companyID],
    queryFn: () => getInvoices(companyID),
    staleTime: 5000,
  });
};

const useBillingInfo = (
  companyID: string,
  userType: 'standard' | 'admin' | 'super-admin',
) => {
  return useQuery({
    queryKey: [...subscriptionsQueries.getBillingInfo, companyID, userType],
    queryFn: () => getBillingInfo(companyID, userType),
    staleTime: 5000,
  });
};

const useSubscriptions = (companyID: string) => {
  return useQuery({
    queryKey: [...subscriptionsQueries.getSubscriptions, companyID],
    queryFn: () => getSubscriptions(companyID),
    staleTime: 5000,
  });
};

const useSetupIntent = (companyID: string) => {
  return useQuery({
    queryKey: [...subscriptionsQueries.createSetupIntent, companyID],
    queryFn: () => createSetupIntent(companyID),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: !!companyID,
  });
};

const useUnsubscribeMutation = () => {
  return useMutation({
    mutationFn: async ({
      companyID,
      productID,
    }: {
      companyID: string;
      productID: number;
    }) => await unsubscribe(companyID, productID),
  });
};

export {
  useBillingInfo,
  useInvoices,
  useSetupIntent,
  useSubscriptions,
  useUnsubscribeMutation,
};
